
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator'
import { Customer } from '@/models/accounting/Customer'
import debounce from 'lodash.debounce'
import { PluralResponse } from 'coloquent/dist/response/PluralResponse'
import Testable from '@/mixins/Testable'
import { namespace } from 'vuex-class'

const CustomersCache = namespace('accountingCustomers')

@Component
export default class CustomerSelector extends Mixins(Testable) {
	@Prop({ required: true })
	value!: Customer | null

	@Prop({ required: false, default: () => [] })
	preloadedCustomers!: Array<Customer>

	@CustomersCache.Getter('peekAll')
	peekAll

	@CustomersCache.Mutation('cache')
	cache

	internalModel: Customer | null = null

	debouncedQuery: () => Promise<void> | undefined = () => Promise.resolve()
	query: string = ''
	loading: boolean = false

	get customers() {
		return this.peekAll().filter((customer: Customer) => {
			if (!this.query?.trim()) return true
			const lcaseQuery = this.query.trim().toLowerCase()
			return (
				customer.name?.toLowerCase().includes(lcaseQuery) ||
				customer.reference?.toLowerCase().includes(lcaseQuery)
			)
		})
	}

	get includingInitial() {
		return ([...this.preloadedCustomers, this.value, ...this.customers].filter(Boolean) as Array<Customer>).reduce(
			(unique, customer) =>
				unique.find((c) => customer.getApiId() === c.getApiId()) ? unique : [...unique, customer],
			[] as Array<Customer>
		)
	}

	async created() {
		this.debouncedQuery = debounce(this.getCustomers, 300)
		this.internalModel = this.value
	}

	private async getCustomers() {
		this.loading = true
		try {
			const alreadyInStore =
				this.value &&
				this.includingInitial.find((c) => c.search === this.query)?.getApiId() === this.value?.getApiId()

			if (alreadyInStore) {
				this.loading = false
				return
			}
			if (this.query && this.query.trim().length >= 3) {
				const queryBuilder = Customer.where('search', this.query)
				const result = (await queryBuilder.get()) as PluralResponse
				this.cache(result.getData() as Array<Customer>)
			} else if (!this.query || this.query.trim().length === 0) {
				this.clearSearchResults()
				this.$emit('input', null)
			}
		} finally {
			this.loading = false
		}
	}

	clearSearchResults() {
		this.query = ''
		this.$emit('clear')
	}

	emitInput(customer: Customer | null) {
		// Check to ensure only actual clients are emitted
		if (typeof customer?.getApiId !== 'function') {
			return
		}

		this.$emit('input', customer)
	}

	formatRelationTypes(types: Array<string>) {
		return types?.map((type) => this.$t(`invoiceCreator.companyType.${type}`)).join(', ')
	}

	@Watch('query')
	triggerQuery() {
		this.debouncedQuery()
	}

	@Watch('value')
	valueChanged(val) {
		this.internalModel = this.value
	}
}
